import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { subscriptionService } from '../lib/subscriptionService';

/**
 * Auth callback handler that redirects users after email verification
 */
const AuthCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [redirectTo, setRedirectTo] = useState('/account');
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Check if this is coming from a sign-up flow
        // Supabase adds type=signup parameter when redirecting from signup confirmation
        const confirmType = searchParams.get('type');
        const isNewRegistration = confirmType === 'signup';
        
        console.log('Auth callback - confirmation type:', confirmType);
        
        // Get the current session to check if the email has been verified
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('Auth callback error:', error);
          setError('Authentication failed. Please try again.');
          return;
        }
        
        if (!data.session) {
          console.error('No session found after email verification');
          setError('Your session has expired. Please log in again.');
          return;
        }
        
        // User is authenticated, session exists
        console.log('User authenticated, checking subscription requirements');
        
        // Ensure user profile exists in the profiles table using new robust function
        console.log('Ensuring user profile exists for:', data.session.user.id);
        try {
          // Use the new robust profile creation function
          const { data: profileResult, error: profileError } = await supabase
            .rpc('ensure_user_profile', {
              target_user_id: data.session.user.id,
              user_email: data.session.user.email || '',
              user_full_name: data.session.user.user_metadata?.full_name || ''
            });
          
          if (profileError) {
            console.error('Error ensuring profile exists:', profileError);
            
            // Log to audit table
            await supabase.rpc('log_registration_step', {
              target_user_id: data.session.user.id,
              user_email: data.session.user.email,
              step_name: 'auth_callback_profile_creation',
              step_status: 'error',
              error_data: { error: profileError }
            });
            
            setError('Failed to create your profile. Please try again or contact support.');
            return;
          }
          
          console.log('Profile creation result:', profileResult);
          
          // Log successful profile creation step
          await supabase.rpc('log_registration_step', {
            target_user_id: data.session.user.id,
            user_email: data.session.user.email,
            step_name: 'auth_callback_profile_creation',
            step_status: 'success',
            error_data: null,
            extra_metadata: { 
              profile_created: profileResult?.[0]?.profile_created || false,
              confirmation_type: confirmType 
            }
          });
          
        } catch (err) {
          console.error('Unexpected error in profile creation:', err);
          
          // Log the error
          await supabase.rpc('log_registration_step', {
            target_user_id: data.session.user.id,
            user_email: data.session.user.email,
            step_name: 'auth_callback_profile_creation',
            step_status: 'error',
            error_data: { error: err }
          });
        }
        
        // Check if the user has any existing subscription
        const userSubscription = await subscriptionService.getUserSubscriptionStatus(data.session.user.id);
        console.log('User subscription status:', userSubscription);
        
        // Check if paywall is enabled
        const settings = await subscriptionService.getPaywallSettings();
        console.log('Paywall settings:', settings);
        
        // Direct to subscription page if:
        // 1. This is a new registration (type=signup) AND
        // 2. Paywall is enabled AND
        // 3. User doesn't have an active subscription
        if (
          (isNewRegistration || userSubscription !== 'active') && 
          settings.paywall_enabled
        ) {
          console.log('Redirecting to subscription page - new user or no active subscription');
          setRedirectTo('/subscription');
        } else {
          console.log('Redirecting to account page - existing user or paywall disabled');
          setRedirectTo('/account');
        }
      } catch (err) {
        console.error('Unexpected error in auth callback:', err);
        setError('An unexpected error occurred. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    handleEmailConfirmation();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-rose-600 mb-4">Confirming your email...</h1>
          <div className="w-16 h-16 border-t-4 border-rose-500 border-solid rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Please wait while we confirm your account</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-md bg-white shadow-lg rounded-lg">
          <h1 className="text-2xl font-semibold text-rose-600 mb-4">Verification Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex justify-center">
            <a 
              href="/login" 
              className="bg-rose-600 text-white px-6 py-2 rounded-full hover:bg-rose-700 transition-colors"
            >
              Return to Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Redirect to the appropriate page based on paywall settings
  return <Navigate to={redirectTo} replace />;
};

export default AuthCallback;
